import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './TeacherFreeTier.css';

/**
 * TeacherShare — frictionless colleague-share page for free-tier teachers.
 *
 * Three one-click channels (copy link / email / SMS), all backed by an
 * attributed share URL minted server-side. Plus a referral progress
 * widget so the teacher can see their viral chain.
 */

const PERK_TIERS = [
  { count: 1, label: 'Early access to new features' },
  { count: 3, label: 'Advanced analytics preview' },
  { count: 5, label: 'Free year of School-tier when you upgrade' },
];

const buildEmailSubject = (teacherName) =>
  `${teacherName ? teacherName + ' is using Tappy — try it' : 'Try Tappy'}`;

const buildEmailBody = (teacherName, shareUrl) =>
  [
    `Hi,`,
    ``,
    `I've been using Tappy with my class — it's a free AI voice tutor that works like one-on-one help for every student. The free tier is genuinely free (1 class, 30 students, unlimited sessions, no credit card).`,
    ``,
    `Try it here — students don't need accounts:`,
    shareUrl,
    ``,
    teacherName ? `– ${teacherName}` : '',
  ].join('\n');

const buildSmsBody = (teacherName, shareUrl) =>
  `${teacherName ? teacherName + ' here. ' : ''}Trying Tappy with my class — free AI voice tutor, no student accounts needed: ${shareUrl}`;

const TeacherShare = ({ user }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [loadingShare, setLoadingShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    outbound_shares: 0,
    visits: 0,
    signed_up: 0,
    activated: 0,
    referred: [],
  });
  const [statsLoaded, setStatsLoaded] = useState(false);
  const navigate = useNavigate();

  const teacherName = user?.name || user?.username || '';

  const mintShareUrl = async (channel = 'link') => {
    setLoadingShare(true);
    try {
      const response = await axios.post('/api/teacher/referrals/share-link', {
        channel,
      });
      if (response.data?.share_url) {
        setShareUrl(response.data.share_url);
        return response.data.share_url;
      }
    } catch (err) {
      // Soft-fail: still render a basic ref-only URL so the share works.
      const userId = user?.id;
      if (userId) {
        const fallback =
          `${window.location.origin.replace('app.', '')}` +
          `/teachers?ref=${userId}&utm_source=teacher_referral`;
        setShareUrl(fallback);
        return fallback;
      }
    } finally {
      setLoadingShare(false);
    }
    return null;
  };

  // Auto-mint a default copy-link URL on page load so the most common
  // path (paste into Slack/email) is one click.
  useEffect(() => {
    mintShareUrl('link');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load referral roster + counts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/teacher/referrals/my');
        if (!cancelled && response.data?.success) {
          setStats({
            outbound_shares: response.data.outbound_shares || 0,
            visits: response.data.visits || 0,
            signed_up: response.data.signed_up || 0,
            activated: response.data.activated || 0,
            referred: response.data.referred || [],
          });
          setStatsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) setStatsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopy = async () => {
    const url = shareUrl || (await mintShareUrl('link'));
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback: user can copy from the displayed input
    }
  };

  const handleEmail = async () => {
    const url = await mintShareUrl('email');
    if (!url) return;
    const subject = encodeURIComponent(buildEmailSubject(teacherName));
    const body = encodeURIComponent(buildEmailBody(teacherName, url));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleSms = async () => {
    const url = await mintShareUrl('sms');
    if (!url) return;
    const body = encodeURIComponent(buildSmsBody(teacherName, url));
    // iOS uses ?body=; Android also accepts it with the modern sms: scheme.
    window.location.href = `sms:?body=${body}`;
  };

  const nextPerk =
    PERK_TIERS.find((p) => stats.signed_up < p.count) ||
    PERK_TIERS[PERK_TIERS.length - 1];
  const reachedPerk = PERK_TIERS.filter((p) => stats.signed_up >= p.count).pop();

  return (
    <div className="teacher-lite-page">
      <div className="teacher-lite-topbar">
        <div className="teacher-lite-brand">
          <img
            src="/images/TAPPY-logo-black.png"
            alt="Tappy"
            className="teacher-lite-logo"
          />
          <span className="teacher-lite-tier-badge">Free</span>
        </div>
        <button
          className="teacher-btn-ghost"
          onClick={() => navigate('/teacher/dashboard')}
        >
          ← Dashboard
        </button>
      </div>

      <div className="teacher-lite-main">
        <section className="teacher-lite-section">
          <h1 className="teacher-lite-heading">Send Tappy to a colleague</h1>
          <p className="teacher-lite-subhead">
            They get the same free-forever tier. You get early access to new
            features for every colleague who tries it.
          </p>

          {/* Channel buttons */}
          <div className="teacher-share-channels">
            <button
              className="teacher-btn-primary"
              onClick={handleCopy}
              disabled={loadingShare}
            >
              {copied ? '✓ Copied' : 'Copy link'}
            </button>
            <button
              className="teacher-btn-secondary"
              onClick={handleEmail}
              disabled={loadingShare}
            >
              Email
            </button>
            <button
              className="teacher-btn-secondary"
              onClick={handleSms}
              disabled={loadingShare}
            >
              Text message
            </button>
          </div>

          {/* Display the URL so users can copy from it as a fallback */}
          {shareUrl && (
            <div className="teacher-code-display teacher-code-inline">
              <div className="teacher-code-label">Your share link</div>
              <div
                className="teacher-code-value"
                style={{
                  fontSize: 13,
                  fontFamily: "'SF Mono', Menlo, monospace",
                  letterSpacing: 0,
                  wordBreak: 'break-all',
                }}
              >
                {shareUrl}
              </div>
            </div>
          )}

          <p
            className="teacher-lite-empty-hint"
            style={{ marginTop: 12 }}
          >
            Every link is uniquely tagged so we can credit the colleagues
            you bring in. No personal data shared.
          </p>
        </section>

        {/* Referral progress */}
        <section className="teacher-lite-section">
          <h2 className="teacher-lite-subheading">Your referral progress</h2>
          <div className="teacher-share-stats">
            <PerkStat label="Shares sent" value={stats.outbound_shares} />
            <PerkStat label="Visits" value={stats.visits} />
            <PerkStat label="Signed up" value={stats.signed_up} />
            <PerkStat label="Active" value={stats.activated} />
          </div>

          <div className="teacher-lite-cta-card">
            <h3 className="teacher-lite-cta-title">
              {reachedPerk
                ? `Unlocked: ${reachedPerk.label}`
                : `Next perk in ${Math.max(nextPerk.count - stats.signed_up, 0)} more colleague${
                    nextPerk.count - stats.signed_up === 1 ? '' : 's'
                  }`}
            </h3>
            <p>
              {nextPerk.count > stats.signed_up
                ? `${nextPerk.count} signups → ${nextPerk.label}.`
                : 'You\'ve reached every milestone — thank you for spreading the word.'}
            </p>
          </div>
        </section>

        {/* Roster of referred teachers */}
        <section className="teacher-lite-section">
          <h2 className="teacher-lite-subheading">Teachers you've referred</h2>
          {!statsLoaded ? (
            <div className="teacher-lite-empty">Loading…</div>
          ) : stats.referred.length === 0 ? (
            <div className="teacher-lite-empty">
              <p>No referrals yet — share a link above to start.</p>
              <p className="teacher-lite-empty-hint">
                When a colleague signs up using your link, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="teacher-lite-roster">
              {stats.referred.map((r, idx) => (
                <div key={idx} className="teacher-lite-student-row">
                  <div className="teacher-lite-student-name">
                    {r.first_name}
                    {r.school_name ? (
                      <span
                        style={{
                          color: '#9ca3af',
                          fontWeight: 400,
                          fontSize: 13,
                          marginLeft: 8,
                        }}
                      >
                        · {r.school_name}
                      </span>
                    ) : null}
                  </div>
                  <div className="teacher-lite-student-stats">
                    {r.status === 'activated'
                      ? `Active · ${r.active_students} student${r.active_students === 1 ? '' : 's'}`
                      : r.status === 'has_class'
                      ? 'Set up a class'
                      : 'Signed up'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const PerkStat = ({ label, value }) => (
  <div
    style={{
      padding: '14px 16px',
      background: '#f9fafb',
      borderRadius: 10,
      textAlign: 'center',
    }}
  >
    <div
      style={{
        fontSize: 28,
        fontWeight: 700,
        color: '#297A92',
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginTop: 4,
      }}
    >
      {label}
    </div>
  </div>
);

export default TeacherShare;
